/**
 * useResumeLint — one server-lint pass shared by the Resume Editor's rail
 * score, its Scorecard chip badge and the Scorecard modal, so all three agree
 * from the moment the editor opens (see `utils/resumeLint.ts` for the why).
 *
 * Cadence: once when `enabled` turns true (editor open + resume loaded), and
 * again whenever `refresh()` is called (after a Save). No fetch while typing.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { api } from '../services/api';
import { LINT_INITIAL, isScoreComputing, lintReducer, runLint } from '../utils/resumeLint';
import type { LintFinding } from '../utils/scorecard';
import type { ResumeJSON } from '../types';

export interface UseResumeLintResult {
  findings: LintFinding[] | null;
  loading: boolean;
  error: boolean;
  /** The headline score is not lint-inclusive yet — show it as computing. */
  computing: boolean;
  /** Re-run the lint pass (call after a save). */
  refresh: () => void;
  /** Adopt findings computed elsewhere (the modal's own pass / a row recheck). */
  apply: (findings: LintFinding[] | null) => void;
}

export function useResumeLint(opts: {
  /** Editor open AND the resume loaded — nothing is fetched before that. */
  enabled: boolean;
  /** Addresses the endpoint only; the body carries the resume that is linted. */
  lintName: string;
  /**
   * Identity of the resume RECORD being edited. Switching resumes without
   * closing the editor is a fresh resume: the previous resume's findings are
   * dropped so the overall score blanks (rather than reporting a number that
   * belongs to a different resume) until the new pass lands. Optional — omit to
   * key freshness on open/close alone. A brand-new resume gaining a name on its
   * first save is NOT a switch (it is the same resume) and does not blank.
   */
  resumeKey?: string;
  /** Live resume, read at fetch time so keystrokes never re-fire the request. */
  resumeRef: MutableRefObject<ResumeJSON>;
}): UseResumeLintResult {
  const { enabled, lintName, resumeKey, resumeRef } = opts;
  const [state, dispatch] = useReducer(lintReducer, LINT_INITIAL);
  const [runId, setRunId] = useState(0);
  const nameRef = useRef(lintName);
  nameRef.current = lintName;
  // Last resume the current findings belong to (undefined = nothing linted yet).
  const lintedKeyRef = useRef<string | undefined>(resumeKey);

  useEffect(() => {
    if (!enabled || !nameRef.current) return;
    // A switch to a DIFFERENT saved resume invalidates what's on screen. Reset
    // first, so `start` below leaves the state as "first pass, no number yet"
    // instead of a refresh that keeps the old resume's score visible.
    if (lintedKeyRef.current && resumeKey && lintedKeyRef.current !== resumeKey) {
      dispatch({ type: 'reset' });
    }
    lintedKeyRef.current = resumeKey;
    let cancelled = false;
    runLint(
      () => api.lintResume(nameRef.current, resumeRef.current).then((d) => d?.findings || []),
      dispatch,
      () => cancelled,
    );
    return () => { cancelled = true; };
  }, [enabled, runId, resumeKey, resumeRef]);

  // Closing the editor clears the state so a reopen starts from scratch.
  useEffect(() => { if (!enabled) dispatch({ type: 'reset' }); }, [enabled]);

  const refresh = useCallback(() => setRunId((n) => n + 1), []);
  const apply = useCallback((findings: LintFinding[] | null) => dispatch({ type: 'apply', findings }), []);

  return { ...state, computing: isScoreComputing(state), refresh, apply };
}
